import React from 'react';
import { RefreshCw } from 'lucide-react';

interface SensitivityResult {
  pct: number;
  claim: number;
}

interface AnalysisPanelProps {
  // Sensitivity Analysis
  sensitivityParam: string;
  sensitivityLoading: boolean;
  sensitivityBaseClaim: number;
  sensitivityMin: number;
  sensitivityMax: number;
  sensitivitySpread: number;
  sensitivitySpreadPct: number;
  sensitivityResults?: SensitivityResult[];
  sensitivityRunAt?: string;
  onSensitivityParamChange: (param: string) => void;
  onRunSensitivity: () => void;
  
  // Monte Carlo
  monteCarloLoading: boolean;
  monteCarloResults?: {
    mean: number;
    median: number;
    std_dev: number;
    ci_95_low: number;
    ci_95_high: number;
    min: number;
    max: number;
  };
  monteCarloIterations?: number;
  monteCarloRunAt?: string;
  onRunMonteCarlo: () => void;
  
  // Auto analysis
  autoAnalysisRunning: boolean;
}

export const AnalysisPanel: React.FC<AnalysisPanelProps> = ({
  sensitivityParam,
  sensitivityLoading,
  sensitivityBaseClaim,
  sensitivityMin,
  sensitivityMax,
  sensitivitySpread,
  sensitivitySpreadPct,
  sensitivityResults,
  sensitivityRunAt,
  onSensitivityParamChange,
  onRunSensitivity,
  monteCarloLoading,
  monteCarloResults,
  monteCarloIterations,
  monteCarloRunAt,
  onRunMonteCarlo,
  autoAnalysisRunning,
}) => {
  return (
    <div className="space-y-6">
      {/* Sensitivity Analysis */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Sensitivity Analysis</h3>
          <div className="flex items-center gap-2">
            <select
              value={sensitivityParam}
              onChange={(e) => onSensitivityParamChange(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500"
            >
              <option value="payroll">Payroll</option>
              <option value="revenue">Revenue</option>
              <option value="cogs">COGS</option>
            </select>
            <button
              onClick={onRunSensitivity}
              disabled={sensitivityLoading || autoAnalysisRunning}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50 transition"
            >
              {sensitivityLoading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Run
            </button>
          </div>
        </div>
        <div className="p-6">
          {sensitivityResults && sensitivityResults.length > 0 ? (
            <div>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Base Claim</p>
                  <p className="text-lg font-bold text-gray-900">${sensitivityBaseClaim.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Min</p>
                  <p className="text-lg font-bold text-red-600">${sensitivityMin.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Max</p>
                  <p className="text-lg font-bold text-green-600">${sensitivityMax.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Spread</p>
                  <p className="text-lg font-bold text-gray-900">{sensitivitySpreadPct.toFixed(1)}%</p>
                </div>
              </div>
              {sensitivityRunAt && (
                <p className="text-xs text-gray-500 text-right">
                  Last run: {new Date(sensitivityRunAt).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Click "Run" to perform sensitivity analysis</p>
          )}
        </div>
      </div>

      {/* Monte Carlo Simulation */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Monte Carlo Simulation</h3>
          <button
            onClick={onRunMonteCarlo}
            disabled={monteCarloLoading || autoAnalysisRunning}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg disabled:opacity-50 transition"
          >
            {monteCarloLoading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Run (1000 iterations)
          </button>
        </div>
        <div className="p-6">
          {monteCarloResults ? (
            <div>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Mean</p>
                  <p className="text-lg font-bold text-gray-900">${monteCarloResults.mean.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">Median</p>
                  <p className="text-lg font-bold text-gray-900">${monteCarloResults.median.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">95% CI Low</p>
                  <p className="text-lg font-bold text-red-600">${monteCarloResults.ci_95_low.toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-500">95% CI High</p>
                  <p className="text-lg font-bold text-green-600">${monteCarloResults.ci_95_high.toLocaleString()}</p>
                </div>
              </div>
              <div className="text-xs text-gray-500 text-right">
                {monteCarloIterations && <span>{monteCarloIterations.toLocaleString()} iterations • </span>}
                {monteCarloRunAt && <span>Last run: {new Date(monteCarloRunAt).toLocaleString()}</span>}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Click "Run" to perform Monte Carlo simulation</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisPanel;
